import { type CSSProperties } from "react";

import { extractEmailPreviewHtml, parseAssistantDraftFromHtml, type NewsletterCampaign } from "./newsletterUtils";

type NewsletterDetailPreviewVariant = "draft" | "sent";

type NewsletterDetailPreviewProps = {
  campaign: Pick<NewsletterCampaign, "name" | "html_body" | "status"> | null;
  variant: NewsletterDetailPreviewVariant;
};

const CLASSIC_MINIATURE_SCALE = 0.5;
const ASSISTANT_PREVIEW_WIDTH = 620;
const ASSISTANT_PREVIEW_SCALES: Record<NewsletterDetailPreviewVariant, number> = {
  draft: 0.46,
  sent: 0.36,
};

function classicPreviewHtml(campaign: NewsletterDetailPreviewProps["campaign"]) {
  return extractEmailPreviewHtml(campaign?.html_body || "");
}

function assistantPreviewHtml(campaign: NewsletterDetailPreviewProps["campaign"]) {
  return extractEmailPreviewHtml(campaign?.html_body || "");
}

function hasAssistantMetadata(campaign: NewsletterDetailPreviewProps["campaign"]) {
  return Boolean(campaign?.html_body && parseAssistantDraftFromHtml(campaign.html_body));
}

function NewsletterScaledAssistantPreview({
  campaign,
  variant,
}: {
  campaign: NewsletterDetailPreviewProps["campaign"];
  variant: NewsletterDetailPreviewVariant;
}) {
  const html = assistantPreviewHtml(campaign);
  if (!html) {
    return (
      <div className={`newsletter-detail-preview-empty is-${variant}`} aria-label="Aucun aperçu disponible">
        Aucun aperçu disponible
      </div>
    );
  }

  const scale = ASSISTANT_PREVIEW_SCALES[variant];
  const innerMinHeight = Math.ceil(860 / scale);
  return (
    <div
      className={`newsletter-scaled-assistant-preview is-${variant}`}
      aria-label={`Aperçu réduit de ${campaign?.name || "la newsletter"}`}
    >
      <div
        className="newsletter-scaled-assistant-preview-content"
        style={
          {
            "--newsletter-scaled-assistant-width": `${ASSISTANT_PREVIEW_WIDTH}px`,
            "--newsletter-scaled-assistant-scale": scale,
            "--newsletter-scaled-assistant-min-height": `${innerMinHeight}px`,
          } as CSSProperties
        }
        dangerouslySetInnerHTML={{ __html: html }}
      />
    </div>
  );
}

export function NewsletterDetailPreview({ campaign, variant }: NewsletterDetailPreviewProps) {
  if (hasAssistantMetadata(campaign)) {
    return <NewsletterScaledAssistantPreview campaign={campaign} variant={variant} />;
  }

  const html = classicPreviewHtml(campaign);
  if (!html) {
    return (
      <div className={`newsletter-detail-preview-empty is-${variant}`} aria-label="Aucun aperçu disponible">
        Aucun aperçu disponible
      </div>
    );
  }

  return (
    <div
      className={`newsletter-classic-miniature is-${variant}`}
      aria-label={`Aperçu réduit de ${campaign?.name || "la newsletter"}`}
    >
      <div
        className="newsletter-classic-miniature-content"
        style={{ "--newsletter-classic-miniature-scale": CLASSIC_MINIATURE_SCALE } as CSSProperties}
        dangerouslySetInnerHTML={{ __html: html }}
      />
    </div>
  );
}
